import { Injectable } from '@angular/core';
import { Observable, Observer, throwError } from 'rxjs';
import { TokenLogin, TokenUsuario } from 'src/app/shared/models/login-response';
import { WsNotificationService, StartStopResult } from './ws-notification.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import AppResponseError from 'src/app/shared/models/app-response-error';
import { environment } from '../../../environments/environment';
import { identifyAmplitudeUser, resetAmplitudeUser } from 'src/app/shared/services/amplitude.config';
import { EMPRESAS_DEMONSTRACAO } from 'src/app/shared/util/constants';

export interface RefreshRequest {
  empresaContratanteID: number;
  email: string;
  refreshToken: string;
  dataLogin: Date;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  redirectUrl: string = null;
  private websocketAberto = false;

  constructor(
    private wsService: WsNotificationService,
    private apiService: ApiService,
    private toastr: ToastrService
  ) {

    // if we does not have a token, we are logged off
    if (!this.token) {
      this.stopWs();
    } else {
      identifyAmplitudeUser(this.credentials);
    }
  }

  /**
   * Verifica se o usuário atual possui um token válido salvo no localStorage,
   */
  get isLoggedIn(): Observable<boolean> {
    return this.validateToken()
      .pipe(map((logado: boolean) => {
        if (!logado) {
          this.stopWs();
        }
        return logado;
      }));
  }

  /**
   * Dado o token salvo no localStorage, verifica se ele pode acessar a área do admin
   */
  get canAccessAdmin(): boolean {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    if (!token || !token.usuario || !token.usuario.areaPermissoes) {
      return false;
    }

    const retorno = token.usuario.areaPermissoes.findIndex(x => x.nome.startsWith('AdminProduto')) > -1;
    return retorno;
  }

  podeAcessarContratantes(): boolean {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario.podeAcessarContratantes ?? false;
  }

  /**
   * Pega o token no localStorage
   */
  get token(): string {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.accessToken ?? undefined;
  }

  /**
   * Pega o Refreshtoken no localStorage
   */
  get refreshToken(): string {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.refreshToken ?? undefined;
  }

  get userId(): number {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario?.id ?? undefined;
  }

  get username(): string {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario?.nome ?? '';
  }

  get credentials(): TokenUsuario {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario as TokenUsuario;
  }

  get isEmpresaDemonstracao(): boolean {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token') ?? '') || {}
    ) as TokenLogin;

    return !!EMPRESAS_DEMONSTRACAO.find(empresaDemonstracao =>
      empresaDemonstracao.id === token?.usuario.contratante.id
      && empresaDemonstracao.ambiente === environment.name)  
  }

  get tokenLogin(): TokenLogin | null {
    return JSON.parse(localStorage.getItem('gsp_access_token')) || null;
  }

  get utilizaEditor(): boolean {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario?.contratante?.utilizaEditor ?? false;
  }

  /**
   * Salva o Token no localStorage
   * @param token Token a ser salvo
   */
  storeCredentials(token: TokenLogin): void {
    localStorage.setItem('gsp_access_token', JSON.stringify(token));
    if(!this.websocketAberto)
      this.startWs();
    identifyAmplitudeUser(token.usuario);
  }


  /**
   * Apaga os dados no localStorage e fecha o WebSocket
   */
  doLogout(): Observable<void> {
    this.clearStorage();
    this.stopWs();
    resetAmplitudeUser();
    return new Observable(obs => obs.next());
  }

  /**
   * Apaga os dados setados no localStorage
   */
  clearStorage(): void {
    const token = localStorage.getItem('gsp_access_token');

    if (token) {
      localStorage.removeItem('gsp_access_token');
    }
  }

  /**
   * Perguntar ao servidor se o token salvo no localStorage é valido
   */
  private validateToken(): Observable<boolean> {

    return new Observable<boolean>(obs => {

      if (!this.token) {
        obs.next(false);
        obs.complete();
        return;
      }

      this.apiService.validarToken()
        .subscribe({
          next: (data: TokenUsuario) => {
            // TODO: Salvar os outros dados que estão em 'data' e são retornados da validação
            obs.next(true);
            if(!this.websocketAberto)
              this.startWs();
            obs.complete();
            return;
          }, error: (err: AppResponseError) => {
            if (err.statusCode < 500) {
              obs.next(false);
              obs.complete();
              // Se o token recebido é sujo, apaga ele
              this.clearStorage();
              this.toastr.warning('Faça login novamente para continuar', 'Login expirou!');
              return;
            } else {
              obs.next(true);
              obs.complete();
            }
          }
        } as Observer<TokenUsuario>);
    });
  }

  private logDebug(msg: any): void {
    if (environment.production != true) {
      console.log(msg);
    }
  }

  renewCredentials(newEmpresaContratanteID?: number): Observable<TokenLogin> {
    const tokenUsuario = this.credentials;

    if (!tokenUsuario || !tokenUsuario.email)
      return throwError({ error: 'Credenciais vazias!', statuscode: 401 });

    const refr = {
      empresaContratanteID: newEmpresaContratanteID ?? tokenUsuario?.contratante?.id ?? 0,
      email: tokenUsuario.email,
      refreshToken: this.refreshToken,
      dataLogin: tokenUsuario.dataLogin
    } as RefreshRequest;

    return this.apiService.refreshToken(refr).pipe(
      map(x => {
        this.logDebug('Reiniciando WS');
        this.stopWs();
        this.startWs();

        // Identifica novamente o usuário no Amplitude com a contratante ativa atualizada, para que eventos disparados após a troca não fiquem atribuidos a contratante anterior
        identifyAmplitudeUser(x.usuario);

        return x;
      }));
  }

  /**
   * Abre uma conexão com o WebSocket
   */
  private startWs(): void {

    if (!this.token) {
      this.apiService.loggarErro(
        // { conteudo: 'token vazio na fn startWs', sessao: 'auth-service -> startWs', logLevel: 'Warning' }
        {
          conteudo: '2MVwkIprT4vw/OBzxr3YaGT7pv3tz22KxiPcVeLyIx4=',
          sessao: 'tNl7Jqv/DQdKvkw6NJ39hyN/pGy31F8ag9XDka4yfS0=',
          logLevel: 'HfQBA5jWa2+Y6R9vV1Mw/A==',
          criptografado: true
        }
      ).subscribe();
      return;
    }
    const tempObs = this.wsService.start(this.token);
    if (!tempObs ) {
      // this.apiService.loggarErro(
        //   // { conteudo: 'wsService.start retornou nulo no lugar de um Observable', sessao: 'auth-service -> startWs', logLevel: 'Warning' }
        //   {
      //     conteudo: 'MQbJRPga1mVJkU3u57fj3XTO/4mpDnYq8znqOFEknmIdbqxvbk6vX3BDCkBBmZJ9BT6d7nAIWfG6XP5jPua+9Q==',
      //     sessao: 'tNl7Jqv/DQdKvkw6NJ39hyN/pGy31F8ag9XDka4yfS0=',
      //     logLevel: 'HfQBA5jWa2+Y6R9vV1Mw/A==',
      //     criptografado: true
      //   }
      // )
      //   .subscribe();
      return;
    }
    tempObs.subscribe({
      next: (s: StartStopResult) => {
        this.websocketAberto = true;
        this.logDebug('WS Connected');
      }, error: (err: StartStopResult) => {
        this.websocketAberto = false;
        this.logDebug('Erro ao conectar ao WS');
        this.logDebug(err);
        this.apiService.loggarErro(
          // { conteudo: err, sessao: 'auth-service -> startWs', logLevel: 'Warning' }
          {
            sessao: 'tNl7Jqv/DQdKvkw6NJ39hyN/pGy31F8ag9XDka4yfS0=',
            conteudo: err, logLevel: 'HfQBA5jWa2+Y6R9vV1Mw/A==', criptografado: false
          }
        ).subscribe();
      }
    } as Observer<StartStopResult>);
  }

  /**
   * Encerra uma conexão com o WebSocket
   */
  private stopWs(): void {
    this.wsService.stop()
      .subscribe({
        next: (s: StartStopResult) => {
          this.websocketAberto = false;
          this.logDebug('WS Disconnected');
        },
        error: (err: StartStopResult) => {
          this.websocketAberto = false;
          this.logDebug('Erro ao desconectar do WS');
          this.logDebug(err);
          this.apiService.loggarErro(
            // { conteudo: err, sessao: 'auth-service -> stopWs', logLevel: 'Warning' }
            {
              sessao: 'tNl7Jqv/DQdKvkw6NJ39hwxhWEbu2xCxzx7coE6DLGk=',
              conteudo: err, logLevel: 'HfQBA5jWa2+Y6R9vV1Mw/A==', criptografado: false
            }
          ).subscribe();
        }
      } as Observer<StartStopResult>);
  }

  get perfilIds(): number[] {
    const token = (
      JSON.parse(localStorage.getItem('gsp_access_token')) || {}
    ) as TokenLogin;

    return token?.usuario?.perfilIDs ?? undefined;
  }
}
